import { Fragment, useId, useRef, useState } from 'react';
import './RadarChart.css';

const RadarChart = ({ data, size = 300, maxValue = 100, formatTooltip }) => {
  const [activePoint, setActivePoint] = useState(null);
  const tooltipId = useId();
  const activatedPoint = useRef(null);
  const dismissPoint = () => {
    activatedPoint.current = null;
    setActivePoint(null);
  };
  const togglePoint = (index) => {
    const next = activatedPoint.current === index ? null : index;
    activatedPoint.current = next;
    setActivePoint(next);
  };
  const Wrapper = formatTooltip ? 'div' : Fragment;
  const wrapperProps = formatTooltip ? {className: 'radar-interactive', style: {position: 'relative', width: size + 200, maxWidth: '100%'}} : {};
  const labels = Object.keys(data);
  const values = Object.values(data);
  const points = labels.length;

  const angleSlice = (Math.PI * 2) / points;
  const radius = size / 2;

  const coordinates = values.map((value, i) => {
    const angle = i * angleSlice - Math.PI / 2;
    const r = (value / maxValue) * radius;
    return {
      x: radius + r * Math.cos(angle),
      y: radius + r * Math.sin(angle),
    };
  });

  const polygonPoints = coordinates.map((c) => `${c.x},${c.y}`).join(" ");

  const labelCoordinates = labels.map((_, i) => {
    const angle = i * angleSlice - Math.PI / 2;
    const r = radius + 10;
    return {
      x: radius + r * Math.cos(angle),
      y: radius + r * Math.sin(angle),
      angle,
    };
  });

  return (
    <Wrapper {...wrapperProps}>
    <svg width={size + 200} height={size + 100} viewBox={formatTooltip ? `0 0 ${size + 200} ${size + 100}` : undefined} style={formatTooltip ? {display: 'block', width: '100%', height: 'auto'} : undefined}>
      <g transform={`translate(120,50)`}>
        {[...Array(5)].map((_, i) => (
          <circle
            key={i}
            cx={radius}
            cy={radius}
            r={(radius / 5) * (i + 1)}
            fill="none"
            stroke="#ddd"
          />
        ))}

        {labels.map((_, i) => {
          const angle = i * angleSlice - Math.PI / 2;
          return (
            <line
              key={i}
              x1={radius}
              y1={radius}
              x2={radius + radius * Math.cos(angle)}
              y2={radius + radius * Math.sin(angle)}
              stroke="#888"
            />
          );
        })}

        <polygon
          points={polygonPoints}
          fill="rgba(0,128,0,0.3)"
          stroke="#008000"
          strokeWidth={2}
        />

        {coordinates.map((c, idx) => (
          <circle key={idx} cx={c.x} cy={c.y} r={4} fill="#008000" />
        ))}

        {labelCoordinates.map((c, idx) => (
          <text
            key={idx}
            x={c.x}
            y={c.y}
            textAnchor={
              Math.cos(c.angle) > 0.1
                ? "start"
                : Math.cos(c.angle) < -0.1
                ? "end"
                : "middle"
            }
            dominantBaseline="middle"
            style={{ fontSize: 12, fontWeight: "bold", fill: "#333" }}
          >
            {labels[idx]}
          </text>
        ))}
        {formatTooltip && coordinates.map((c, idx) => (
          <circle key={`hit-${idx}`} className="radar-point-hit" cx={c.x} cy={c.y} r={22}
            fill="transparent" role="button" tabIndex={0}
            aria-label={formatTooltip(labels[idx], values[idx])}
            aria-describedby={activePoint === idx ? tooltipId : undefined}
            onMouseEnter={() => setActivePoint(idx)}
            onMouseLeave={dismissPoint}
            onFocus={() => setActivePoint(idx)}
            onBlur={dismissPoint}
            onClick={() => togglePoint(idx)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') dismissPoint();
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                togglePoint(idx);
              }
            }}
          />
        ))}
      </g>
    </svg>
    {formatTooltip && activePoint !== null && coordinates[activePoint] && (
      <div id={tooltipId} role="tooltip" className="radar-point-tooltip"
        style={{left: `clamp(0px, ${(coordinates[activePoint].x + 120) / (size + 200) * 100}% - 140px, max(0px, 100% - 280px))`, top: `${(coordinates[activePoint].y + 78) / (size + 100) * 100}%`}}>
        {formatTooltip(labels[activePoint], values[activePoint])}
      </div>
    )}
    </Wrapper>
  );
};

export default RadarChart;
